import { useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { MultiIdCombobox } from '@/components/ui/multi-id-combobox';
import { Textarea } from '@/components/ui/textarea';
import { useIncidentRoster } from 'components/IAP/hooks/incidentRoster/useIncidentRoster';
import { useActualIncidentId } from 'components/IAP/hooks/commonHooks';
import { useGetPositionsForIncident } from 'hooks/incident/useRoster';
import {
  NotificationPriority,
  NotificationType,
  useCreateNotification,
} from 'hooks/useNotification';
import { buildTaskOpenAppMessage } from 'components/MapPage/CorporateViewTabs/NotificationsTab/appMessageTaskLinks';

type TaskRejectExecutionDialogProps = {
  open: boolean;
  actionId: string;
  taskName?: string;
  /** Request Approval / Request Completion stamp user, when known. */
  defaultRequesterUserId?: string | null;
  onOpenChange: (open: boolean) => void;
  /** Create the new Draft tip (cleared signatures, Planned) before notify. */
  onConfirmDraft: () => void | Promise<void>;
  confirmPending?: boolean;
  title?: string;
  description?: string;
  messageLabel?: string;
  messagePlaceholder?: string;
  confirmLabel?: string;
};

/**
 * Confirm reject (execution or completion): new Draft tip with current content,
 * then notify chosen recipients with a typed message.
 */
export function TaskRejectExecutionDialog({
  open,
  actionId,
  taskName = '',
  defaultRequesterUserId,
  onOpenChange,
  onConfirmDraft,
  confirmPending = false,
  title = 'Reject Execution',
  description = 'Rejecting creates a new Draft version autopopulated with all content from the current version so no work is lost. Approval signatures are cleared and status returns to Planned.',
  messageLabel = 'Message to requester',
  messagePlaceholder = 'Explain why execution was rejected…',
  confirmLabel = 'Reject and create Draft',
}: TaskRejectExecutionDialogProps) {
  const incidentId = useActualIncidentId();
  const { data: roster = [] } = useIncidentRoster();
  const { data: positions = [] } = useGetPositionsForIncident();
  const notify = useCreateNotification();
  const [userIds, setUserIds] = useState<string[]>([]);
  const [positionIds, setPositionIds] = useState<string[]>([]);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!open) return;
    setMessage('');
    setPositionIds([]);
    const requester = (defaultRequesterUserId ?? '').trim();
    setUserIds(requester ? [requester] : []);
  }, [open, defaultRequesterUserId]);

  const userOptions = useMemo(
    () =>
      roster
        .filter((member) => member.active !== 0 && member.userId)
        .map((member) => ({
          value: member.userId,
          label: member.userName || member.emailAddress || member.userId,
        })),
    [roster]
  );

  const positionOptions = useMemo(
    () =>
      positions
        .filter((position) => !position.isResourcePosition)
        .map((position) => ({
          value: position.id,
          label: position.name,
        })),
    [positions]
  );

  const send = async () => {
    const trimmed = message.trim();
    const fromPositions = roster
      .filter((member) =>
        member.incidentPositions.some((position) =>
          positionIds.includes(position.positionId)
        )
      )
      .map((member) => member.userId);
    const recipients = [...new Set([...userIds, ...fromPositions])].filter(
      Boolean
    );
    if (!incidentId || !trimmed || recipients.length === 0) return;

    await onConfirmDraft();
    const path = `/iap/${incidentId}?openTab=Actions&openAction=${actionId}`;
    await notify.mutateAsync({
      incidentId,
      acknowledgementRequired: false,
      autoArchiveDate: null,
      priority: NotificationPriority.INFO,
      emailSubject: null,
      emailMessage: null,
      smsMessage: null,
      teamsMessage: null,
      phoneMessage: null,
      appMessage: buildTaskOpenAppMessage({
        prefix: `${trimmed}\n\n`,
        taskName,
        path,
      }),
      userIds: recipients,
      teamIds: [],
      // notifications.source_item_id FK → item_reference; actions are not rows there.
      sourceItemId: null,
      sourceItemType: null,
      scheduledDate: null,
      notificationTypes: [NotificationType.IN_APP],
      aors: [],
    });
    setUserIds([]);
    setPositionIds([]);
    setMessage('');
    onOpenChange(false);
  };

  const pending = notify.isPending || confirmPending;
  const canSend =
    message.trim().length > 0 && (userIds.length > 0 || positionIds.length > 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="dark tw:sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="tw:flex tw:flex-col tw:gap-3">
          <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
            {description}
          </p>
          <div>
            <Label htmlFor="reject-execution-message" className="tw:mb-1">
              {messageLabel}
            </Label>
            <Textarea
              id="reject-execution-message"
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              placeholder={messagePlaceholder}
              rows={4}
            />
          </div>
          <div>
            <div className="tw:mb-1 tw:text-xs tw:font-medium tw:text-muted-foreground">
              Users
            </div>
            <MultiIdCombobox
              options={userOptions}
              value={userIds}
              onChange={setUserIds}
              placeholder="Select users…"
            />
          </div>
          <div>
            <div className="tw:mb-1 tw:text-xs tw:font-medium tw:text-muted-foreground">
              Positions
            </div>
            <MultiIdCombobox
              options={positionOptions}
              value={positionIds}
              onChange={setPositionIds}
              placeholder="Select positions…"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={pending || !canSend}
            onClick={() => void send()}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
